import React from 'react';
import { motion } from 'framer-motion';
import { XCircleIcon } from 'lucide-react';
export function ProblemSection() {
  const scrollToPricing = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const mistakes = ['Uno aprendió mirando, sin que nadie le explicara el sistema de verdad.', 'Otro sabe las reglas básicas pero nunca entendió cuándo cantar y cuándo no.', 'El de la pareja toma decisiones solo, sin coordinar nada con su compañero.', 'Todos bluffean por impulso, no por estrategia.', 'Nadie sabe leer al rival: se guían por la carta, no por la persona.'];
  return <section className="py-24 bg-truco-dark relative border-t border-truco-surface" data-id="element-141">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-142">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="text-center mb-16" data-id="element-143">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-truco-cream mb-8" data-id="element-144">
            El problema no es que tenés mala suerte con las cartas.
          </h2>

          <div className="space-y-6 text-lg text-truco-creamMuted text-left" data-id="element-145">
            <p data-id="element-146">Si jugás al Truco en Argentina, sabés de qué hablamos.</p>
            <p data-id="element-147">
              Llega el asado del domingo. Se arma la mesa. Y en algún momento
              pasa algo que no se dice en voz alta pero todos sienten:
            </p>
            <p className="text-xl font-heading font-bold text-truco-gold text-center py-4" data-id="element-148">
              Hay uno que sabe. Y hay uno que pierde.
            </p>
            <p data-id="element-149">
              Y si estás leyendo esto, probablemente en más de una ocasión
              fuiste el segundo.
            </p>
            <p data-id="element-150">
              Quizás empezás bien la partida. Pero cuando el rival te canta
              Falta Envido, algo se congela. No sabés si tenés para pagar. No
              sabés si te están chamuyando. Y terminás diciendo "No quiero"
              aunque quizás tenías para ganar.
            </p>
            <p data-id="element-151">
              O peor: decís "Quiero" cuando no tenías nada y perdés 15 puntos de
              una sola mano.
            </p>
            <p data-id="element-152">
              O jugás en pareja y tu compañero hace algo que no entendés. O vos
              hacés algo que lo confunde a él. Y entre los dos se regalan puntos
              que nadie tendría que haber regalado.
            </p>
            <p className="font-bold text-truco-cream" data-id="element-153">
              Y lo frustrante no es perder. Es no entender por qué perdiste.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="bg-truco-surface border border-truco-red/20 rounded-2xl p-8 mb-16 shadow-2xl" data-id="element-154">
          <h3 className="text-2xl font-heading font-bold text-truco-cream mb-6 text-center" data-id="element-155">
            Lo que pasa en la mayoría de las mesas argentinas
          </h3>
          <ul className="space-y-4" data-id="element-156">
            {mistakes.map((mistake, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-157">
                <XCircleIcon className="w-6 h-6 text-truco-red shrink-0 mt-0.5" data-id="element-158" />
                <span className="text-truco-creamMuted" data-id="element-159">{mistake}</span>
              </li>)}
          </ul>
          <div className="mt-8 pt-8 border-t border-truco-dark text-center" data-id="element-160">
            <p className="text-lg text-truco-cream" data-id="element-161">
              Y encima hay siempre uno en la mesa que parece que sabe algo que
              los demás no saben. Gana con cartas mediocres. Lee cuando estás
              mintiendo. Sube la apuesta justo cuando no conviene.
            </p>
            <p className="text-xl font-heading font-bold text-truco-gold mt-4" data-id="element-162">
              Ese jugador no nació sabiendo. Aprendió un sistema.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} data-id="element-163">
          <h3 className="text-3xl font-heading font-bold text-truco-cream mb-6 text-center" data-id="element-164">
            El cortocircuito real del Truco argentino
          </h3>
          <p className="text-center text-truco-creamMuted mb-10 text-lg" data-id="element-165">
            El problema no es la suerte ni las cartas que te tocan.
            <br data-id="element-166" />
            El Truco se pierde por tres razones concretas:
          </p>

          <div className="space-y-8 mb-12" data-id="element-167">
            <div className="bg-truco-surface/50 p-6 rounded-xl border border-truco-gold/10" data-id="element-168">
              <h4 className="text-xl font-heading font-bold text-truco-gold mb-2" data-id="element-169">
                Razón 1: No tenés un sistema de decisión claro
              </h4>
              <p className="text-truco-creamMuted" data-id="element-170">
                para el Envido y el Truco. Tomás decisiones por emoción o por
                impulso. Y la emoción en el Truco es el camino más corto a
                regalar puntos.
              </p>
            </div>
            <div className="bg-truco-surface/50 p-6 rounded-xl border border-truco-gold/10" data-id="element-171">
              <h4 className="text-xl font-heading font-bold text-truco-gold mb-2" data-id="element-172">
                Razón 2: No sabés mentir con estructura
              </h4>
              <p className="text-truco-creamMuted" data-id="element-173">
                Mentís cuando tenés miedo (y se nota) o decís la verdad cuando
                podrías haber ganado sin mostrar nada. La mentira en el Truco no
                es trampa: es la mitad del juego. Y se aprende.
              </p>
            </div>
            <div className="bg-truco-surface/50 p-6 rounded-xl border border-truco-gold/10" data-id="element-174">
              <h4 className="text-xl font-heading font-bold text-truco-gold mb-2" data-id="element-175">
                Razón 3: Si jugás en pareja, jugás como dos individuos
              </h4>
              <p className="text-truco-creamMuted" data-id="element-176">
                en vez de como un equipo. Tu compañero toma decisiones sin saber
                qué tenés vos. Vos hacés lo mismo. Y el resultado es el caos:
                cantos que se pisan, señas que no llegan, puntos que se van
                solos.
              </p>
            </div>
          </div>

          <div className="text-center mb-12" data-id="element-177">
            <p className="text-2xl font-heading font-bold text-truco-cream" data-id="element-178">
              No es falta de talento. No es mala suerte.
            </p>
            <p className="text-2xl font-heading font-bold text-truco-red mt-2" data-id="element-179">
              Es falta de un sistema que funcione de verdad.
            </p>
          </div>

          <div className="flex justify-center" data-id="element-180">
            <motion.a href="#pricing" onClick={scrollToPricing} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="inline-block w-full sm:w-auto bg-truco-red hover:bg-truco-redHover text-white font-heading font-bold text-lg px-8 py-4 rounded-lg shadow-[0_0_30px_rgba(196,30,58,0.3)] transition-all text-center" data-id="element-181">
              🔥 QUIERO DEJAR DE PERDER — MOSTRAME EL SISTEMA →
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>;
}